import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "../components/PageHeader";
import { useAuthStore } from "../stores/authStore";
import { Navigate } from "react-router-dom";
import {
  listActivityLogs,
  getActivityLogStats,
  exportActivityLogs,
  cleanupActivityLogs,
  type ActivityLogEntry,
  type ActivityLogFilter,
  type ActivityLogStats,
  type ActivityCategory,
  CATEGORY_DISPLAY_NAMES,
  CATEGORY_COLORS,
} from "../services/adminService";

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString();
}

// Format relative time
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Category badge component
function CategoryBadge({ category }: { category: ActivityCategory }) {
  const colorClasses: Record<string, string> = {
    blue: "bg-blue-500/20 text-blue-400",
    purple: "bg-purple-500/20 text-purple-400",
    green: "bg-accent-500/20 text-accent-400",
    orange: "bg-orange-500/20 text-orange-400",
    gray: "bg-surface-600 text-surface-300",
  };

  const color = CATEGORY_COLORS[category] || "gray";

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[color]}`}>
      {CATEGORY_DISPLAY_NAMES[category] || category}
    </span>
  );
}

export function AdminActivityLogsPage() {
  const { user } = useAuthStore();
  
  // Check if user is admin (TradeCraft_Admin role)
  const isAdmin = user?.roles?.some((role) => role.name === "TradeCraft_Admin");
  
  // State
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [stats, setStats] = useState<ActivityLogStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filter state
  const [filter, setFilter] = useState<ActivityLogFilter>({
    page: 1,
    limit: 25,
  });
  
  // UI state
  const [selectedActivity, setSelectedActivity] = useState<ActivityLogEntry | null>(null);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(90);
  const [isExporting, setIsExporting] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listActivityLogs(filter);
      setActivities(response.activities || []);
      setTotalPages(response.pages || 1);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch activity logs");
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const statsData = await getActivityLogStats();
      setStats(statsData);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isAdmin) {
      fetchActivities();
      fetchStats();
    }
  }, [isAdmin, fetchActivities, fetchStats]);

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportActivityLogs(filter);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity-logs-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export");
    } finally {
      setIsExporting(false);
    }
  };

  // Handle cleanup
  const handleCleanup = async () => {
    setIsCleaning(true);
    try {
      const result = await cleanupActivityLogs(cleanupDays);
      alert(`Deleted ${result.deleted} old entries`);
      setShowCleanupModal(false);
      fetchActivities();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cleanup");
    } finally {
      setIsCleaning(false);
    }
  };

  // Handle filter changes
  const updateFilter = (updates: Partial<ActivityLogFilter>) => {
    setFilter((prev) => ({ ...prev, ...updates, page: 1 }));
  };

  // Redirect non-admins
  if (!isAdmin) {
    return <Navigate to="/portfolio" replace />;
  }

  return (
    <div>
      <PageHeader title="Activity Logs" subtitle="View and manage user activity logs" />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Stats Cards */}
        {!isLoadingStats && stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="glass-card p-4">
              <div className="text-sm text-surface-400">Total Entries</div>
              <div className="text-2xl font-bold text-surface-100">{(stats.total ?? 0).toLocaleString()}</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-sm text-surface-400">Today</div>
              <div className="text-2xl font-bold text-accent-400">{(stats.today ?? 0).toLocaleString()}</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-sm text-surface-400">Categories</div>
              <div className="text-2xl font-bold text-primary-400">{Object.keys(stats.by_category ?? {}).length}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Category filter */}
            <div>
              <label className="block text-xs text-surface-400 mb-1">Category</label>
              <select
                className="px-3 py-2 border border-surface-600 rounded-lg bg-surface-800 text-surface-100 text-sm focus:ring-2 focus:ring-primary-500"
                value={filter.category?.[0] || ""}
                onChange={(e) => updateFilter({ category: e.target.value ? [e.target.value as ActivityCategory] : undefined })}
              >
                <option value="">All Categories</option>
                <option value="auth">Authentication</option>
                <option value="strategy">Strategy</option>
                <option value="portfolio">Portfolio</option>
                <option value="brokerage">Brokerage</option>
                <option value="system">System</option>
              </select>
            </div>

            {/* Search by username/email */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-surface-400 mb-1">Search</label>
              <input
                type="text"
                placeholder="Username or email..."
                className="w-full px-3 py-2 border border-surface-600 rounded-lg bg-surface-800 text-surface-100 text-sm focus:ring-2 focus:ring-primary-500"
                value={filter.username || ""}
                onChange={(e) => updateFilter({ username: e.target.value || undefined })}
              />
            </div>

            {/* Date range */}
            <div>
              <label className="block text-xs text-surface-400 mb-1">From</label>
              <input
                type="date"
                className="px-3 py-2 border border-surface-600 rounded-lg bg-surface-800 text-surface-100 text-sm focus:ring-2 focus:ring-primary-500"
                value={filter.from || ""}
                onChange={(e) => updateFilter({ from: e.target.value || undefined })}
              />
            </div>
            <div>
              <label className="block text-xs text-surface-400 mb-1">To</label>
              <input
                type="date"
                className="px-3 py-2 border border-surface-600 rounded-lg bg-surface-800 text-surface-100 text-sm focus:ring-2 focus:ring-primary-500"
                value={filter.to || ""}
                onChange={(e) => updateFilter({ to: e.target.value || undefined })}
              />
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2">
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="px-4 py-2 bg-surface-700 text-surface-200 rounded-lg hover:bg-surface-600 text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {isExporting ? "Exporting..." : "Export CSV"}
              </button>
              <button
                onClick={() => setShowCleanupModal(true)}
                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm font-medium transition-colors"
              >
                Cleanup
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="glass-card bg-red-500/10 border-red-500/30 text-red-400 px-4 py-3 mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Activity Log Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-surface-700">
              <thead className="bg-surface-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Activity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-700">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-surface-400">
                      Loading...
                    </td>
                  </tr>
                ) : activities.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-surface-400">
                      No activity logs found
                    </td>
                  </tr>
                ) : (
                  activities.map((activity) => (
                    <tr
                      key={activity.id}
                      className="hover:bg-surface-800/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedActivity(activity)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-surface-400">
                        <div title={formatDate(activity.created_at)}>{formatRelativeTime(activity.created_at)}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <CategoryBadge category={activity.category} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-surface-100">{activity.title}</div>
                        <div className="text-xs text-surface-400">{activity.activity_type}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-surface-100">
                        {activity.username || activity.email || (activity.user_id ? "Unknown" : "System")}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-surface-400 font-mono text-xs">
                        {activity.ip_address?.split(":")[0] || "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-surface-400">
                        {Object.keys(activity.metadata || {}).length > 0 && (
                          <span className="text-primary-400">View</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-surface-800/50 px-4 py-3 flex items-center justify-between border-t border-surface-700">
            <div className="text-sm text-surface-400">
              Showing {activities.length} of {total.toLocaleString()} entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilter((prev) => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
                disabled={filter.page === 1}
                className="px-3 py-1 bg-surface-700 border border-surface-600 rounded text-sm text-surface-200 hover:bg-surface-600 disabled:opacity-50 transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-surface-300">
                Page {filter.page} of {totalPages}
              </span>
              <button
                onClick={() => setFilter((prev) => ({ ...prev, page: Math.min(totalPages, (prev.page || 1) + 1) }))}
                disabled={filter.page === totalPages}
                className="px-3 py-1 bg-surface-700 border border-surface-600 rounded text-sm text-surface-200 hover:bg-surface-600 disabled:opacity-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Detail Modal */}
      {selectedActivity && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-surface-100">{selectedActivity.title}</h3>
                  <p className="text-sm text-surface-400">{formatDate(selectedActivity.created_at)}</p>
                </div>
                <button
                  onClick={() => setSelectedActivity(null)}
                  className="text-surface-400 hover:text-surface-200 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-surface-400">Category</label>
                    <div className="mt-1">
                      <CategoryBadge category={selectedActivity.category} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-surface-400">Activity Type</label>
                    <div className="text-sm text-surface-100 font-mono">{selectedActivity.activity_type}</div>
                  </div>
                  <div>
                    <label className="text-xs text-surface-400">User</label>
                    <div className="text-sm text-surface-100">
                      {selectedActivity.username || selectedActivity.email || "System"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-surface-400">User ID</label>
                    <div className="text-sm text-surface-100 font-mono text-xs">
                      {selectedActivity.user_id || "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-surface-400">IP Address</label>
                    <div className="text-sm text-surface-100 font-mono">{selectedActivity.ip_address || "-"}</div>
                  </div>
                  <div>
                    <label className="text-xs text-surface-400">Correlation ID</label>
                    <div className="text-sm text-surface-100 font-mono text-xs">
                      {selectedActivity.correlation_id || "-"}
                    </div>
                  </div>
                </div>

                {selectedActivity.description && (
                  <div>
                    <label className="text-xs text-surface-400">Description</label>
                    <div className="text-sm text-surface-100 mt-1">{selectedActivity.description}</div>
                  </div>
                )}

                {selectedActivity.user_agent && (
                  <div>
                    <label className="text-xs text-surface-400">User Agent</label>
                    <div className="text-sm text-surface-300 mt-1 text-xs break-all">{selectedActivity.user_agent}</div>
                  </div>
                )}

                {Object.keys(selectedActivity.metadata || {}).length > 0 && (
                  <div>
                    <label className="text-xs text-surface-400">Metadata</label>
                    <pre className="mt-1 bg-surface-800 rounded p-3 text-xs overflow-x-auto text-surface-200">
                      {JSON.stringify(selectedActivity.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Cleanup Old Logs</h3>
            <p className="text-sm text-surface-400 mb-4">
              This will permanently delete activity logs older than the specified number of days.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Days to Keep
              </label>
              <input
                type="number"
                value={cleanupDays}
                onChange={(e) => setCleanupDays(parseInt(e.target.value) || 90)}
                min={7}
                className="w-full px-3 py-2 border border-surface-600 rounded-lg bg-surface-800 text-surface-100 focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCleanupModal(false)}
                className="px-4 py-2 text-surface-300 hover:bg-surface-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCleanup}
                disabled={isCleaning}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isCleaning ? "Deleting..." : "Delete Old Logs"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminActivityLogsPage;
